import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Col, Row, Space, Tag, Typography } from 'antd';
import { GoldOutlined, RocketOutlined } from '@ant-design/icons';

const { Text, Title, Paragraph } = Typography;

export type AssetFleetMembershipCardProps = {
  assetId: string;
  assetCode?: string | null;
  assetName?: string | null;
  disposed?: boolean;
  canManagePmv: boolean;
  loading?: boolean;
  profile?: {
    id: string;
    operational_status?: string;
    fleet_number?: string | null;
    ownership_type?: string | null;
  } | null;
  profileLoaded?: boolean;
  projectLabel?: string | null;
  expectedRelease?: string | null;
  compact?: boolean;
};

/**
 * Visual Asset ↔ Fleet membership panel.
 * Does not invent PMV authority — only navigates to activate / passport.
 */
export const AssetFleetMembershipCard: React.FC<AssetFleetMembershipCardProps> = ({
  assetId,
  assetCode,
  assetName,
  disposed,
  canManagePmv,
  loading,
  profile,
  profileLoaded,
  projectLabel,
  expectedRelease,
  compact,
}) => {
  const navigate = useNavigate();
  const inFleet = Boolean(profile?.id);

  if (loading) {
    return (
      <Card size="small" loading style={{ marginBottom: compact ? 0 : 16 }}>
        Loading fleet status…
      </Card>
    );
  }

  if (disposed) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: compact ? 0 : 16 }}
        message="Disposed assets cannot join the Fleet Register"
      />
    );
  }

  if (!canManagePmv && !inFleet) {
    return null;
  }

  if (inFleet && profile) {
    return (
      <Card
        size="small"
        style={{
          marginBottom: compact ? 0 : 16,
          borderLeft: '4px solid #1677ff',
          background: '#f0f5ff',
        }}
      >
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} md={14}>
            <Space align="start">
              <GoldOutlined style={{ fontSize: 22, color: '#1677ff', marginTop: 4 }} />
              <div>
                <Space wrap>
                  <Title level={5} style={{ margin: 0 }}>
                    In Fleet Register
                  </Title>
                  <Tag color="blue">PMV</Tag>
                  {profile.operational_status && (
                    <Tag>{profile.operational_status.replace(/_/g, ' ')}</Tag>
                  )}
                </Space>
                <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                  {assetCode ? `${assetCode} · ` : ''}
                  {assetName || 'Equipment'}
                  {profile.fleet_number ? ` · Unit ${profile.fleet_number}` : ''}
                  {profile.ownership_type ? ` · ${profile.ownership_type.replace(/_/g, ' ')}` : ''}
                </Paragraph>
                {projectLabel ? (
                  <Text>
                    Deployed / allocated: <Text strong>{projectLabel}</Text>
                    {expectedRelease ? ` · expected release ${expectedRelease}` : ''}
                  </Text>
                ) : (
                  <Text type="secondary">No active deployment</Text>
                )}
              </div>
            </Space>
          </Col>
          <Col xs={24} md={10} style={{ textAlign: 'right' }}>
            <Space wrap>
              <Button type="primary" onClick={() => navigate(`/asset/pmv/${profile.id}`)}>
                Open Equipment Passport
              </Button>
              <Button onClick={() => navigate('/asset/pmv')}>Fleet Register</Button>
            </Space>
          </Col>
        </Row>
      </Card>
    );
  }

  if (profileLoaded && !inFleet && canManagePmv) {
    return (
      <Card
        size="small"
        style={{
          marginBottom: compact ? 0 : 16,
          borderLeft: '4px solid #fa8c16',
          background: '#fff7e6',
        }}
      >
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} md={16}>
            <Space align="start">
              <RocketOutlined style={{ fontSize: 22, color: '#fa8c16', marginTop: 4 }} />
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  Not in Fleet Register
                </Title>
                <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                  This asset exists in the enterprise register but is not enabled for Plant, Machinery
                  &amp; Vehicle operations. Add it to Fleet to allocate, dispatch, and log daily work.
                </Paragraph>
              </div>
            </Space>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: 'right' }}>
            <Button type="primary" size="large" onClick={() => navigate(`/asset/assets/${assetId}/pmv/activate`)}>
              Add to Fleet
            </Button>
          </Col>
        </Row>
      </Card>
    );
  }

  return null;
};
